import { Children, type ComponentProps, type ReactNode } from "react";
import {
  Box,
  DataTableToolbar,
  DataTableToolbarActions,
  Separator,
  SelectTrigger,
} from "@salestrak/ui";
import { CurrentUserButton } from "#app/components/filters/current-user-button";
import { DateRangePresetPicker } from "#app/components/filters/date-range-preset-picker";
import { FilterToggle } from "#app/components/filters/filter-toggle";
import { getPresetShortLabel, type DatePresetValue } from "#app/lib/utils/date-presets";
import type { Omittable } from "@salestrak/ui";

/**
 * The dashboard's date-range chip: `DateRangePresetPicker` triggered by a
 * `FilterToggle` labeled with the preset's short name. ONE tree for every
 * widget-host toolbar — this toolbar inlines it, and the managed (Team
 * Overview) toolbar renders it standalone with its pill chrome
 * (`shape="pill" offVariant="outline"`).
 */
export function DashboardDateRangePill({
  value,
  onChange,
  shape,
  offVariant,
}: Omittable<{
  value: DatePresetValue;
  onChange: (next: DatePresetValue) => void;
  shape?: ComponentProps<typeof FilterToggle>["shape"];
  offVariant?: ComponentProps<typeof FilterToggle>["offVariant"];
}>) {
  return (
    <DateRangePresetPicker value={value} onChange={onChange} align="start">
      <SelectTrigger chevron={false}
        render={
          <FilterToggle
            size="xs"
            shape={shape}
            offVariant={offVariant}
            icon="calendar"
            label={getPresetShortLabel(value.key)}
            selected={value.key !== "all-time"}
          />
        }
      />
    </DateRangePresetPicker>
  );
}

export type DashboardFilterToolbarProps = Omittable<{
  dateRange: DatePresetValue;
  onDateRangeChange: (next: DatePresetValue) => void;
  /** Only-Me visual state + handlers — wire from `useCurrentUserFilterToggle`
   *  (via `useDashboardScopeState`). */
  onlyMeSelected: boolean;
  onOnlyMePress: () => void;
  onOnlyMeClear: () => void;
  /**
   * Quick-filter side-panel wiring. When `onOpenFilters` is provided the filter
   * chip renders and toggles the shared `FilterPanel` (the workflow/team hosts,
   * which mount an `IssueFilterProvider`). Omit it to hide the chip on surfaces
   * with no panel.
   */
  onOpenFilters?: () => void;
  /** Active count from the shared panel — drives the chip's expanded "N Filters". */
  filterCount?: number;
  onClearFilters?: () => void;
  /** The dashboard's ownership pill — rendered first in the right-aligned row.
   *  It reads as identity (status + its own actions), not a peer control, so it
   *  sits outside both control clusters. */
  leading?: ReactNode;
  /** Customize actions (layout history, Add widget, personalize) — controls
   *  that change the view's persisted setup, grouped after the divider. */
  children?: ReactNode;
}>;

/**
 * The dashboard's filter toolbar — the wrapping "view" that defines the data
 * window every widget reports on (date range + Only-Me; member filtering goes
 * through the shared FilterPanel). It mirrors the issue board/list toolbar: the
 * same `DataTableToolbar` chrome, the same
 * `CurrentUserButton` and `DateRangePresetPicker` controls. Pure
 * and controlled — the route owns the state (`useDashboardScopeState`) and
 * publishes it through `DashboardScopeProvider`; each widget folds it into its own
 * query via `useScopedWhere`. There is no per-widget filter and no data engine.
 */
export function DashboardFilterToolbar({
  dateRange,
  onDateRangeChange,
  onlyMeSelected,
  onOnlyMePress,
  onOnlyMeClear,
  onOpenFilters,
  filterCount = 0,
  onClearFilters,
  leading,
  children,
}: DashboardFilterToolbarProps) {
  // The ephemeral cluster always renders (date/members/Only-Me are
  // unconditional), so the divider only depends on the customize side.
  const hasCustomize = Children.toArray(children).length > 0;

  return (
    <DataTableToolbar>
      <DataTableToolbarActions>
        {leading}
        <Box direction="row" align="center" gap="xs">
          <DashboardDateRangePill value={dateRange} onChange={onDateRangeChange} />
          {/* Member filtering lives in the shared FilterPanel (assignee filter),
              not here — the toolbar keeps only date + Only-Me + the panel chip. */}
          <CurrentUserButton
            selected={onlyMeSelected}
            onClick={onOnlyMePress}
            onClearClick={onOnlyMeClear}
            size="xs"
          />
          {/* Quick-filter chip — opens the shared FilterPanel (the same one the
              board/list use). Hidden on surfaces with no provider. */}
          {onOpenFilters ? (
            <FilterToggle
              size="xs"
              icon="filter"
              count={filterCount}
              selected={filterCount > 0}
              onClick={onOpenFilters}
              onClear={filterCount > 0 ? onClearFilters : undefined}
            />
          ) : null}
        </Box>
        {hasCustomize ? (
          <Separator orientation="vertical" className="h-5" />
        ) : null}
        {hasCustomize ? (
          <Box direction="row" align="center" gap="xs">
            {children}
          </Box>
        ) : null}
      </DataTableToolbarActions>
    </DataTableToolbar>
  );
}
